import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import toast, { Toaster } from "react-hot-toast";
import {
  ArrowLeft,
  Key,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Crown,
  Zap,
  Users,
  Building,
  AlertTriangle,
  Copy,
  Check,
  Loader,
  Star,
} from "lucide-react";
import SessionTimer from "@/components/SessionTimer";

const LICENSE_TIERS = {
  trial: {
    name: "Trial",
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    icon: Clock,
  },
  basic: {
    name: "Basic",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    icon: Key,
  },
  pro: {
    name: "Pro",
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    icon: Zap,
  },
  enterprise: {
    name: "Enterprise",
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10",
    borderColor: "border-emerald-500/30",
    icon: Crown,
  },
};

const LicensePage = () => {
  const { user } = useContext(AuthContext);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [licenseKey, setLicenseKey] = useState("");
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchLicenseData();
  }, []);

  const fetchLicenseData = async () => {
    try {
      const [statusRes, featuresRes] = await Promise.all([
        axios.get("/license/status"),
        axios.get("/license/features"),
      ]);
      setLicenseStatus(statusRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      console.error("Failed to fetch license data:", error);
      toast.error("Failed to load license information");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      toast.error("Please enter a license key");
      return;
    }

    setActivating(true);
    try {
      const response = await axios.post("/license/activate", {
        license_key: licenseKey.trim(),
      });
      toast.success("License activated successfully!");
      setShowActivateModal(false);
      setLicenseKey("");
      fetchLicenseData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate license");
    } finally {
      setActivating(false);
    }
  };

  const formatLicenseKey = (key) => {
    // Format as XXXX-XXXX-XXXX-XXXX
    const cleaned = key.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const parts = [];
    for (let i = 0; i < cleaned.length; i += 4) {
      parts.push(cleaned.slice(i, i + 4));
    }
    return parts.slice(0, 5).join("-");
  };

  const handleKeyChange = (e) => {
    const formatted = formatLicenseKey(e.target.value);
    setLicenseKey(formatted);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 text-cyan-400 animate-spin mx-auto" />
          <p className="mt-3 text-blue-200/70 text-sm">Loading license info...</p>
        </div>
      </div>
    );
  }

  const tier = LICENSE_TIERS[licenseStatus?.license_type] || LICENSE_TIERS.trial;
  const TierIcon = tier.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-blue-900 to-indigo-900">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard" className="text-blue-200 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg font-heading font-semibold text-white">
              License Management
            </h1>
          </div>
          <SessionTimer enabled={true} />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        {/* Current License Card */}
        <div className={`bg-white/10 backdrop-blur-md border ${tier.borderColor} rounded-xl overflow-hidden`}>
          <div className={`${tier.bgColor} px-6 py-4 border-b ${tier.borderColor}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <TierIcon className={`w-6 h-6 ${tier.color}`} />
                <div>
                  <h2 className={`text-lg font-semibold ${tier.color}`}>
                    {tier.name} License
                  </h2>
                  {licenseStatus?.organization_name && (
                    <p className="text-sm text-blue-200/70">
                      {licenseStatus.organization_name}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {licenseStatus?.is_valid ? (
                  <span className="flex items-center space-x-1 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Active</span>
                  </span>
                ) : (
                  <span className="flex items-center space-x-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                    <XCircle className="w-4 h-4" />
                    <span>Expired</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* License Details */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-200/70 text-sm mb-1">
                  <Clock className="w-4 h-4" />
                  <span>Expires</span>
                </div>
                <p className="text-white font-medium">
                  {licenseStatus?.expires_at
                    ? new Date(licenseStatus.expires_at).toLocaleDateString()
                    : "Never (Perpetual)"}
                </p>
                {licenseStatus?.days_remaining !== null && (
                  <p className={`text-sm mt-1 ${
                    licenseStatus.days_remaining <= 7 ? "text-red-400" :
                    licenseStatus.days_remaining <= 30 ? "text-amber-400" : "text-emerald-400"
                  }`}>
                    {licenseStatus.days_remaining} days remaining
                  </p>
                )}
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-200/70 text-sm mb-1">
                  <Users className="w-4 h-4" />
                  <span>Users</span>
                </div>
                <p className="text-white font-medium">
                  {licenseStatus?.max_users === -1
                    ? "Unlimited"
                    : `Up to ${licenseStatus?.max_users}`}
                </p>
              </div>

              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-200/70 text-sm mb-1">
                  <Building className="w-4 h-4" />
                  <span>Clients</span>
                </div>
                <p className="text-white font-medium">
                  {licenseStatus?.max_clients === -1
                    ? "Unlimited"
                    : `Up to ${licenseStatus?.max_clients}`}
                </p>
              </div>
            </div>

            {/* Trial Warning */}
            {licenseStatus?.is_trial && (
              <div className="flex items-start space-x-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-200 font-medium">Trial License</p>
                  <p className="text-amber-200/70 text-sm mt-1">
                    You have {licenseStatus.trial_days_remaining} days remaining in your trial.
                    Activate a license key to unlock all features and remove limitations.
                  </p>
                </div>
              </div>
            )}

            {/* Activate Button */}
            <button
              onClick={() => setShowActivateModal(true)}
              className="w-full py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg hover:from-cyan-600 hover:to-blue-700 transition flex items-center justify-center space-x-2"
              data-testid="activate-license-btn"
            >
              <Key className="w-5 h-5" />
              <span>Activate License Key</span>
            </button>
          </div>
        </div>

        {/* Features Section */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl">
          <div className="px-6 py-4 border-b border-white/10">
            <h3 className="text-white font-semibold flex items-center space-x-2">
              <Star className="w-5 h-5 text-amber-400" />
              <span>Features</span>
            </h3>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Enabled Features */}
              {features?.enabled_features?.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-start space-x-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg"
                >
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium text-sm">{feature.name}</p>
                    <p className="text-blue-200/60 text-xs mt-0.5">{feature.description}</p>
                  </div>
                </div>
              ))}

              {/* Disabled Features */}
              {features?.disabled_features?.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-start space-x-3 p-3 bg-white/5 border border-white/10 rounded-lg opacity-60"
                >
                  <XCircle className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-slate-300 font-medium text-sm">{feature.name}</p>
                    <p className="text-blue-200/40 text-xs mt-0.5">{feature.description}</p>
                    <p className="text-cyan-400 text-xs mt-1">Upgrade to unlock</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upgrade CTA */}
        {features?.upgrade_available && (
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-6 text-center">
            <Crown className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <h3 className="text-white font-semibold text-lg mb-2">
              Upgrade Your License
            </h3>
            <p className="text-blue-200/70 text-sm mb-4 max-w-md mx-auto">
              Unlock more features, increase limits, and get priority support with a Pro or Enterprise license.
            </p>
            <a
              href="mailto:sales@fingerprintvs.com?subject=License%20Upgrade%20Inquiry"
              className="inline-flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              <span>Contact Sales</span>
            </a>
          </div>
        )}
      </main>

      {/* Activate License Modal */}
      {showActivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-slate-800 border border-white/20 rounded-xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                <Key className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Activate License</h3>
                <p className="text-blue-200/70 text-sm">Enter your license key</p>
              </div>
            </div>

            <form onSubmit={handleActivate}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  License Key
                </label>
                <input
                  type="text"
                  value={licenseKey}
                  onChange={handleKeyChange}
                  placeholder="FPVS-XXXX-XXXX-XXXX-XXXX"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200/40 font-mono text-center tracking-wider focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  data-testid="license-key-input"
                  maxLength={24}
                />
                <p className="text-blue-200/50 text-xs mt-2 text-center">
                  Format: FPVS-XXXX-XXXX-XXXX-XXXX
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowActivateModal(false);
                    setLicenseKey("");
                  }}
                  className="flex-1 py-2 px-4 border border-white/20 text-white rounded-lg hover:bg-white/10 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={activating || licenseKey.length < 24}
                  className="flex-1 py-2 px-4 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  data-testid="submit-license-btn"
                >
                  {activating ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Activate</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicensePage;
